/**
 * @file BlogSchemes.tsx
 * @description Мини-схемы вариантов панели «Блог» для карточек-вариантов
 * `OptionCards` (phase19.16; решение пользователя 2026-10-12 «Нужна
 * визуализация» раскладок): раскладка ленты (сетка 2, сетка 3, список), вид
 * статьи (широкая, узкая обложка, без обложки) и место оглавления (сбоку,
 * сверху). Каждая схема — запись реестра (вариант → фигуры), а не ветка.
 *
 * Примитивы `Scheme`/`Photo`/`Line` и схема обложки шапки ленты
 * (`CoverLayoutScheme`) — в общем модуле `schemes/CoverSchemes` (общие с
 * баннером шапки категории).
 */

import type { SiteBlogOptions } from "@chaiberi/shared";
import type { ReactNode } from "react";

import { Line, Photo, Scheme } from "../schemes/CoverSchemes";

/** Карточка статьи в ленте: обложка и две строки. */
const Card = ({ x, w }: { x: number; w: number }) => (
  <g>
    <Photo x={x} y={4} w={w} h={18} />
    <Line x={x} y={26} w={w * 0.9} />
    <Line x={x} y={31} w={w * 0.6} />
  </g>
);

/** Строка списка: обложка слева, текст справа. */
const Row = ({ y }: { y: number }) => (
  <g>
    <Photo x={4} y={y} w={14} h={9} />
    <Line x={22} y={y + 1} w={34} />
    <Line x={22} y={y + 5} w={22} />
  </g>
);

const FEED: Readonly<Record<SiteBlogOptions["feedLayout"], ReactNode>> = {
  grid2: (
    <g>
      <Card x={6} w={24} />
      <Card x={34} w={24} />
    </g>
  ),
  grid3: (
    <g>
      <Card x={3} w={17} />
      <Card x={23.5} w={17} />
      <Card x={44} w={17} />
    </g>
  ),
  list: (
    <g>
      <Row y={2} />
      <Row y={15} />
      <Row y={28} />
    </g>
  ),
};

/** Текст статьи: заголовок и абзац колонкой от `x` шириной `w`. */
const Body = ({ x, y, w }: { x: number; y: number; w: number }) => (
  <g>
    <Line x={x} y={y} w={w * 0.7} h={3} />
    <Line x={x} y={y + 6} w={w} />
    <Line x={x} y={y + 10} w={w} />
    <Line x={x} y={y + 14} w={w * 0.8} />
  </g>
);

const ARTICLE: Readonly<Record<SiteBlogOptions["articleLayout"], ReactNode>> = {
  wide: (
    <g>
      <Photo x={0} y={2} w={64} h={16} />
      <Body x={14} y={21} w={36} />
    </g>
  ),
  narrow: (
    <g>
      <Photo x={14} y={2} w={36} h={14} />
      <Body x={14} y={20} w={36} />
    </g>
  ),
  none: <Body x={14} y={8} w={36} />,
};

/** Оглавление — рамка со строками пунктов. */
const Toc = ({ x, y, w, h }: { x: number; y: number; w: number; h: number }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx={2} fill="none" stroke="currentColor" strokeOpacity={0.45} />
    <Line x={x + 3} y={y + 3} w={w - 8} />
    <Line x={x + 3} y={y + 7} w={w - 10} />
  </g>
);

const TOC: Readonly<Record<SiteBlogOptions["toc"]["place"], ReactNode>> = {
  side: (
    <g>
      <Body x={4} y={6} w={36} />
      <Toc x={45} y={6} w={16} h={14} />
    </g>
  ),
  top: (
    <g>
      <Toc x={14} y={2} w={36} h={12} />
      <Body x={14} y={18} w={36} />
    </g>
  ),
};

export const FeedLayoutScheme = ({ layout }: { layout: SiteBlogOptions["feedLayout"] }) => <Scheme>{FEED[layout]}</Scheme>;
export const ArticleLayoutScheme = ({ layout }: { layout: SiteBlogOptions["articleLayout"] }) => <Scheme>{ARTICLE[layout]}</Scheme>;
export const TocPlaceScheme = ({ place }: { place: SiteBlogOptions["toc"]["place"] }) => <Scheme>{TOC[place]}</Scheme>;
